import React, { lazy, Suspense } from "react";
import { ROLES } from "@/constants/roles";
import LoadingScreen from "@/components/LoadingScreen";
import { Navigate } from "react-router-dom";
import TransactionGroups from "@/pages/admin/TransactionGroups";
import PaymentStatus from "@/pages/admin/PaymentStatus";
import TrainerFeedback from "@/components/trainer/TrainerFeedback";
import CohortAssessments from "@/components/cohort/CohortAssessments";

// Lazy load components
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ParticipantDashboard = lazy(() => import("@/pages/user/Dashboard"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const CohortDashboard = lazy(() => import("@/pages/admin/CohortDashboard"));
const CohortMessages = lazy(() => import("@/pages/admin/CohortMessages"));
const AddNewCohort = lazy(() => import("@/pages/admin/AddNewCohort"));
const EditCohort = lazy(() => import("@/pages/admin/EditCohort"));
const AccountingDashboard = lazy(() => import("@/pages/accounting/Dashboard"));
const ParticipantProgress = lazy(
  () => import("@/pages/participant/ParticipantProgress")
);
const ParticipantProfiles = lazy(
  () => import("@/pages/participant-profiles/ParticipantProfiles")
);
const ClientProfiles = lazy(
  () => import("@/pages/admin/client-profile/ClientProfiles")
);
const AddNewClient = lazy(
  () => import("@/pages/admin/client-profile/AddNewClient")
);
const EditClient = lazy(
  () => import("@/pages/admin/client-profile/EditClient")
);
const ActiveCohort = lazy(() => import("@/pages/active-cohort/ActiveCohort"));
const CompletedCohort = lazy(
  () => import("@/pages/completed-cohort/CompletedCohort")
);
const CohortTransactions = lazy(
  () => import("@/pages/admin/CohortTransactions")
);
const AttendanceRegister = lazy(
  () => import("@/pages/admin/AttendanceRegister")
);
const CohortAssessmentReport = lazy(
  () => import("@/pages/admin/CohortAssessmentReport")
);
const UserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const AssessmentTemplates = lazy(
  () => import("@/pages/admin/AssessmentTemplates")
);
const AddAssessmentTemplate = lazy(
  () => import("@/pages/admin/AddAssessmentTemplate")
);
const AddTransactionTemplate = lazy(
  () => import("@/pages/admin/AddTransactionTemplate")
);
const EditTransactionTemplate = lazy(
  () => import("@/pages/admin/EditTransactionTemplate")
);
const Reports = lazy(() => import("@/pages/admin/Reports"));
const FinancialStatements = lazy(
  () => import("@/pages/user/FinancialStatements")
);
const LearningArea = lazy(() => import("@/pages/user/LearningArea"));
const SetupPassword = lazy(() => import("@/pages/SetupPassword"));
const PublicAssessmentForm = lazy(
  () => import("@/pages/public/PublicAssessmentForm")
);
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));

// Wrap component with Suspense
const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component />
  </Suspense>
);

export const RoleBasedRoutes = [
  {
    path: "/",
    exact: true,
    roles: {
      [ROLES.USER]: withSuspense(Dashboard),
      [ROLES.ADMIN]: withSuspense(Dashboard),
      [ROLES.TRAINER]: withSuspense(Dashboard),
    },
  },
  {
    path: "/dashboard",
    exact: true,
    roles: {
      [ROLES.USER]: withSuspense(ParticipantDashboard),
      [ROLES.ADMIN]: withSuspense(AdminDashboard),
      [ROLES.TRAINER]: withSuspense(AccountingDashboard),
    },
  },
  {
    path: "/learning-area",
    exact: true,
    roles: {
      [ROLES.USER]: withSuspense(LearningArea),
      [ROLES.ADMIN]: withSuspense(LearningArea),
      [ROLES.TRAINER]: withSuspense(LearningArea),
    },
  },
  {
    path: "/cohort-dashboard/:cohortId",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(CohortDashboard),
      [ROLES.TRAINER]: withSuspense(CohortDashboard),
    },
  },
  // Cohort Dashboard Sub-routes
  {
    path: "/cohort-dashboard/:cohortId/participant-progress",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(ParticipantProgress),
      [ROLES.TRAINER]: withSuspense(ParticipantProgress),
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/participant-profiles",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(ParticipantProfiles),
      [ROLES.TRAINER]: withSuspense(ParticipantProfiles),
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/user-management",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(UserManagement),
      [ROLES.TRAINER]: (
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-4">User Management</h1>
          <p className="text-gray-600">Manage users for this cohort.</p>
        </div>
      ),
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/transactions",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(CohortTransactions),
      [ROLES.TRAINER]: withSuspense(CohortTransactions),
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/feedback",
    exact: true,
    roles: {
      [ROLES.ADMIN]: <TrainerFeedback isAdmin={true} />,
      [ROLES.TRAINER]: <TrainerFeedback isAdmin={false} />,
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/messages",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(CohortMessages),
      [ROLES.TRAINER]: withSuspense(CohortMessages),
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/assessments",
    exact: true,
    roles: {
      [ROLES.ADMIN]: <CohortAssessments isAdmin={true} />,
      [ROLES.TRAINER]: <CohortAssessments isAdmin={false} />,
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/attendance",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(AttendanceRegister),
      [ROLES.TRAINER]: withSuspense(AttendanceRegister),
    },
  },
  {
    path: "/cohort-dashboard/:cohortId/report",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(CohortAssessmentReport),
      [ROLES.TRAINER]: withSuspense(CohortAssessmentReport),
    },
  },
  {
    path: "/add-new-cohort",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(AddNewCohort),
    },
  },
  {
    path: "/edit-cohort/:cohortId",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(EditCohort),
    },
  },
  {
    path: "/participant-progress",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(ParticipantProgress),
    },
  },
  {
    path: "/participant-profiles",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(ParticipantProfiles),
    },
  },
  {
    path: "/client-profiles",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(ClientProfiles),
    },
  },
  {
    path: "/add-client",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(AddNewClient),
    },
  },
  {
    path: "/edit-client/:id",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(EditClient),
    },
  },
  {
    path: "/active-cohorts",
    exact: true,
    roles: {
      [ROLES.TRAINER]: withSuspense(ActiveCohort),
      [ROLES.ADMIN]: withSuspense(ActiveCohort),
    },
  },
  {
    path: "/completed-cohorts",
    exact: true,
    roles: {
      [ROLES.TRAINER]: withSuspense(CompletedCohort),
    },
  },
  {
    path: "/user-management",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(UserManagement),
    },
  },
  {
    path: "/assessment-templates",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(AssessmentTemplates),
    },
  },
  {
    path: "/assessment-templates/add",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(AddAssessmentTemplate),
    },
  },
  {
    path: "/assessment-templates/:templateId/edit",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(AddAssessmentTemplate),
    },
  },
  {
    path: "/reports",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(Reports),
    },
  },
  {
    path: "/admin/transaction-groups",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(TransactionGroups),
    },
  },
  {
    path: "/admin/transaction-groups/add",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(AddTransactionTemplate),
    },
  },
  {
    path: "/admin/transaction-groups/:id",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(EditTransactionTemplate),
    },
  },
  {
    path: "/admin/payment-status",
    exact: true,
    roles: {
      [ROLES.ADMIN]: withSuspense(PaymentStatus),
    },
  },
  {
    path: "/course/:cohortId",
    exact: true,
    roles: {
      [ROLES.TRAINER]: withSuspense(ParticipantDashboard),
      [ROLES.ADMIN]: withSuspense(ParticipantDashboard),
    },
  },
  {
    path: "/financial-statements",
    exact: true,
    roles: {
      [ROLES.USER]: withSuspense(FinancialStatements),
      [ROLES.ADMIN]: withSuspense(FinancialStatements),
      [ROLES.TRAINER]: withSuspense(FinancialStatements),
    },
  },
];

// Public routes that don't require authentication
export const publicRoutes = [
  { path: "/", element: <Navigate to="/login" /> },
  { path: "/login", element: withSuspense(Login) },
  { path: "/register", element: withSuspense(Register) },
  { path: "/forgot-password", element: withSuspense(ForgotPassword) },
  { path: "/setup-password", element: withSuspense(SetupPassword) },
  // Public, no-login form (Pre/Post-Assessment + Course Feedback share links).
  { path: "/assessment/:templateId", element: withSuspense(PublicAssessmentForm) },
];
